import { type LeadWorkspaceTabKey } from "../lead-workspace-tabs.tsx"

/** The owner control is part of the persistent header, not of a tab's card. */
const LEAD_HEADER_OWNER_TARGET = "header-owner"

/**
 * The Overview tab's "Lifecycle & Follow-Up" card was removed once the
 * header became the single UI entry point for stage/state/owner/follow-up/
 * appointment (2026-08-30) — the appointment control lives only in the
 * header now, the same way the owner control always has.
 */
const LEAD_HEADER_APPOINTMENT_TARGET = "header-appointment"

const DEFAULT_MISSING_FIELD_TAB: LeadWorkspaceTabKey = "overview"

interface LeadMissingFieldTarget {
  tab: LeadWorkspaceTabKey
  card: string
}

/**
 * Every field name `missing_field_names_for_stage` can emit in ezbuild_back,
 * against the workspace control that writes it. The two lists are the front/back
 * contract — a key the backend adds and this map omits still reaches the user,
 * on the default tab, but marks nothing.
 */
const LEAD_MISSING_FIELD_TARGETS: Record<string, LeadMissingFieldTarget> = {
  customer: { tab: "overview", card: "customer" },
  "customer.first_name": { tab: "overview", card: "customer" },
  "customer.email": { tab: "overview", card: "customer" },
  "customer.phone": { tab: "overview", card: "customer" },
  // The backend names an "either channel" requirement with one combined
  // key (`customer.email_or_phone`), not one of the two field keys — naming
  // one of them as the missing field is what made a phone-answerable lead
  // read as email-only.
  "customer.email_or_phone": { tab: "overview", card: "customer" },
  preferred_contact_method: { tab: "overview", card: "customer" },
  appointment_date: { tab: "overview", card: LEAD_HEADER_APPOINTMENT_TARGET },
  sales_manager: { tab: "overview", card: LEAD_HEADER_OWNER_TARGET },
  address: { tab: "overview", card: "location" },
  // The backend reports the property address as four fields now, not one
  // composite line, so each needs its own target or the banner marks nothing.
  city: { tab: "overview", card: "location" },
  state: { tab: "overview", card: "location" },
  zip_code: { tab: "overview", card: "location" },
  lead_name: { tab: "overview", card: "project" },
  project_type: { tab: "overview", card: "project" },
  scope: { tab: "overview", card: "project" },
  timeline: { tab: "overview", card: "project" },
  budget_range: { tab: "overview", card: "project" }
}

interface LeadMissingField {
  field: string
  tab: LeadWorkspaceTabKey
  /** `null` for a key this app has no control for. */
  card: string | null
  labelKey: string
}

interface LeadMissingFields {
  fields: LeadMissingField[]
  /** How many fields each tab owns, for the tab badges. */
  countByTab: Partial<Record<LeadWorkspaceTabKey, number>>
  /** The tab the workspace opens on arrival. */
  firstTab: LeadWorkspaceTabKey | null
  forCard: (card: string) => LeadMissingField[]
}

const NO_MISSING_FIELDS: LeadMissingFields = {
  fields: [],
  countByTab: {},
  firstTab: null,
  forCard: () => []
}

// i18next reads a dot as a level of nesting, so a nested field name cannot be a
// key segment of its own.
const labelKeyFor = (field: string): string =>
  `leadWorkspace.missing.fields.${field.replace(/\./g, "_")}`

function parseLeadMissingParam(param: string | null): string[] {
  if (!param) return []
  const seen = new Set<string>()
  return param
    .split(",")
    .map((field) => field.trim())
    .filter((field) => {
      if (!field || seen.has(field)) return false
      seen.add(field)
      return true
    })
}

function resolveLeadMissingFields(param: string | null): LeadMissingFields {
  const fields = parseLeadMissingParam(param).map<LeadMissingField>((field) => {
    const target = LEAD_MISSING_FIELD_TARGETS[field]
    return {
      field,
      tab: target?.tab ?? DEFAULT_MISSING_FIELD_TAB,
      card: target?.card ?? null,
      labelKey: labelKeyFor(field)
    }
  })

  if (!fields.length) return NO_MISSING_FIELDS

  const countByTab = fields.reduce<
    Partial<Record<LeadWorkspaceTabKey, number>>
  >((acc, { tab }) => ({ ...acc, [tab]: (acc[tab] ?? 0) + 1 }), {})

  return {
    fields,
    countByTab,
    firstTab: fields[0].tab,
    forCard: (card) => fields.filter((field) => field.card === card)
  }
}

export {
  type LeadMissingField,
  type LeadMissingFields,
  LEAD_HEADER_APPOINTMENT_TARGET,
  LEAD_HEADER_OWNER_TARGET,
  LEAD_MISSING_FIELD_TARGETS,
  NO_MISSING_FIELDS,
  labelKeyFor,
  parseLeadMissingParam,
  resolveLeadMissingFields
}
